import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth';
import { voiceCloningService } from '../services/voiceCloning';
import SampleUploader from '../components/VoiceCloning/SampleUploader';
import CloneStatus from '../components/VoiceCloning/CloneStatus';
import TrainingPrompts from '../components/VoiceCloning/TrainingPrompts';
import TrainingProgress from '../components/VoiceCloning/TrainingProgress';
import QualityTierSelector from '../components/VoiceCloning/QualityTierSelector';
import toast from 'react-hot-toast';

function VoiceCloning() {
  const { user } = useAuthStore();
  const [clones, setClones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClone, setSelectedClone] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCloneName, setNewCloneName] = useState('');
  const [newCloneDescription, setNewCloneDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [trainingConfig, setTrainingConfig] = useState(null);
  const [selectedTargetQuality, setSelectedTargetQuality] = useState('standard');
  const [estimatedTrainingTime, setEstimatedTrainingTime] = useState(60);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewAudioRef = useRef(null);

  // Admins always have voice cloning access
  const isAdmin = user?.role === 'admin';
  const canCloneVoices = isAdmin || user?.voice_cloning_enabled;
  const maxClones = isAdmin ? 100 : (user?.max_cloned_voices || 3);

  useEffect(() => {
    fetchClones();
    fetchTrainingConfig();
  }, []);

  // Auto-refresh training clones
  useEffect(() => {
    const trainingClone = clones.find(c => c.training_status === 'training');
    if (!trainingClone) return;

    const interval = setInterval(() => {
      fetchClones();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [clones]);

  const fetchTrainingConfig = async () => {
    try {
      const config = await voiceCloningService.getTrainingConfig();
      setTrainingConfig(config);
      const defaultTargetQuality = config?.default_target_quality || 'standard';
      const availableProfiles = config?.fine_tuning_profiles || {};
      const resolvedTargetQuality = availableProfiles[defaultTargetQuality]
        ? defaultTargetQuality
        : Object.keys(availableProfiles)[0] || 'standard';
      setSelectedTargetQuality(resolvedTargetQuality);
    } catch (error) {
      console.error('Failed to fetch training config:', error);
    }
  };

  const fetchClones = async () => {
    setIsLoading(true);
    try {
      const data = await voiceCloningService.list();
      setClones(data.voice_clones);
      // Update selected clone if it exists
      if (selectedClone) {
        const updated = data.voice_clones.find(c => c.id === selectedClone.id);
        if (updated) setSelectedClone(updated);
      }
    } catch (error) {
      console.error('Failed to fetch voice clones:', error);
      toast.error('Failed to load voice clones');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClone = async (e) => {
    e.preventDefault();
    if (!newCloneName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    setIsCreating(true);
    try {
      const clone = await voiceCloningService.create(newCloneName, newCloneDescription);
      setClones([clone, ...clones]);
      setSelectedClone(clone);
      setShowCreateModal(false);
      setNewCloneName('');
      setNewCloneDescription('');
      toast.success('Voice clone project created!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create voice clone';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleStartTraining = async (cloneId, targetQuality = null) => {
    try {
      const result = await voiceCloningService.startTraining(cloneId, targetQuality);
      setEstimatedTrainingTime(result.estimated_time_seconds || 60);
      toast.success(
        <div>
          <p className="font-medium">Training started!</p>
          <p className="text-sm opacity-80">
            {result.info}
            {result.target_quality ? ` (${result.target_quality})` : ''}
          </p>
        </div>,
        { duration: 6000 }
      );
      fetchClones();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start training';
      toast.error(message);
    }
  };

  const handleDeleteClone = async (cloneId) => {
    if (!confirm('Are you sure you want to delete this voice clone?')) return;

    try {
      await voiceCloningService.delete(cloneId);
      setClones(clones.filter(c => c.id !== cloneId));
      if (selectedClone?.id === cloneId) {
        setSelectedClone(null);
      }
      toast.success('Voice clone deleted');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete';
      toast.error(message);
    }
  };

  const handleSampleUploaded = (result) => {
    // Refresh the selected clone to update sample count
    if (selectedClone) {
      voiceCloningService.get(selectedClone.id).then(setSelectedClone);
    }
  };

  const handlePreviewClone = async (cloneId) => {
    // If already playing, stop
    if (isPreviewPlaying) {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      setIsPreviewPlaying(false);
      return;
    }

    setIsPreviewLoading(true);
    try {
      const audioUrl = await voiceCloningService.getPreview(cloneId);
      const audio = new Audio(audioUrl);
      previewAudioRef.current = audio;

      audio.oncanplaythrough = () => {
        setIsPreviewLoading(false);
        setIsPreviewPlaying(true);
        audio.play();
      };

      audio.onended = () => {
        setIsPreviewPlaying(false);
        previewAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setIsPreviewLoading(false);
        setIsPreviewPlaying(false);
        previewAudioRef.current = null;
        toast.error('Failed to load preview');
      };

      audio.load();
    } catch (error) {
      console.error('Preview failed:', error);
      setIsPreviewLoading(false);
      toast.error('Failed to generate preview');
    }
  };

  const fineTuningProfiles = trainingConfig?.fine_tuning_profiles || {};
  const lockedFineTuningProfiles = trainingConfig?.locked_fine_tuning_profiles || {};
  const activeProfile =
    fineTuningProfiles[selectedTargetQuality] ||
    fineTuningProfiles[trainingConfig?.default_target_quality] ||
    null;
  const requiredDuration = Number(
    activeProfile?.min_required_duration_seconds ??
    trainingConfig?.min_duration ??
    10
  );

  if (!canCloneVoices) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-16">
          <div className="text-6xl mb-6">🎤</div>
          <h1 className="text-3xl font-bold mb-4">Voice Cloning</h1>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            Clone your voice or any voice to create custom AI voices. 
            This feature is available on Premium, Scale, and Enterprise plans.
          </p>
          <a href="/billing" className="btn btn-primary">
            Upgrade to Premium
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Voice Cloning</h1>
          <p className="text-gray-400">
            Create custom AI voices from audio samples
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-400">
            {clones.length} / {maxClones} clones used
          </span>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
            disabled={clones.length >= maxClones}
          >
            + New Voice Clone
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Voice Clones List */}
        <div className="lg:col-span-1">
          <div className="card">
            <h3 className="font-semibold mb-4">Your Voice Clones</h3>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <span className="spinner w-8 h-8"></span>
              </div>
            ) : clones.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400 mb-4">No voice clones yet</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="text-purple-400 hover:text-purple-300"
                >
                  Create your first clone
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {clones.map((clone) => (
                  <button
                    key={clone.id}
                    onClick={() => setSelectedClone(clone)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedClone?.id === clone.id
                        ? 'bg-purple-600/20 border border-purple-500'
                        : 'bg-gray-700/50 hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{clone.name}</p>
                        <p className="text-sm text-gray-400">
                          {clone.sample_count} samples • {clone.total_sample_duration.toFixed(1)}s
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        clone.training_status === 'completed' 
                          ? 'bg-green-500/20 text-green-400'
                          : clone.training_status === 'training'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {clone.training_status}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Clone Details / Upload */}
        <div className="lg:col-span-2">
          {selectedClone ? (
            <div className="space-y-6">
              {/* Training Progress - shown when training */}
              {selectedClone.training_status === 'training' && (
                <TrainingProgress 
                  clone={selectedClone} 
                  estimatedTime={estimatedTrainingTime}
                  onRefresh={fetchClones}
                />
              )}

              {/* Clone Status - shown when not training */}
              {selectedClone.training_status !== 'training' && (
                <CloneStatus clone={selectedClone} />
              )}

              {/* Quality Tier Selector - shown when pending */}
              {selectedClone.training_status === 'pending' && trainingConfig?.quality_tiers && (
                <QualityTierSelector 
                  tiers={trainingConfig.quality_tiers}
                  currentDuration={selectedClone.total_sample_duration}
                />
              )}

              {/* Fine-tuning target selector */}
              {selectedClone.training_status === 'pending' &&
                (Object.keys(fineTuningProfiles).length > 0 ||
                  Object.keys(lockedFineTuningProfiles).length > 0) && (
                <div className="card">
                  <h3 className="font-semibold mb-2">🎛️ Voice Target</h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Choose training depth. Premium users unlock higher-fidelity targets.
                  </p>
                  <div className="space-y-2">
                    {Object.entries(fineTuningProfiles).map(([profileId, profile]) => {
                      const profileMinDuration = Number(profile.min_required_duration_seconds || 0);
                      const isUnlocked = selectedClone.total_sample_duration >= profileMinDuration;
                      const isSelected = selectedTargetQuality === profileId;
                      return (
                        <button
                          key={profileId}
                          type="button"
                          onClick={() => setSelectedTargetQuality(profileId)}
                          disabled={!isUnlocked}
                          className={`w-full text-left rounded-lg border p-3 transition-all ${
                            isSelected
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-gray-700 bg-gray-800/30'
                          } ${!isUnlocked ? 'opacity-60 cursor-not-allowed' : 'hover:border-purple-400'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{profile.label}</p>
                              <p className="text-xs text-gray-400">
                                Min {profileMinDuration.toFixed(0)}s • Est {(Number(profile.estimated_training_time_seconds || 0) / 60).toFixed(0)}m
                              </p>
                            </div>
                            {isUnlocked ? (
                              <span className="text-green-400 text-xs">Available</span>
                            ) : (
                              <span className="text-yellow-400 text-xs">
                                Need {(profileMinDuration - selectedClone.total_sample_duration).toFixed(1)}s more
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    {Object.entries(lockedFineTuningProfiles).map(([profileId, profile]) => {
                      const profileMinDuration = Number(profile.min_required_duration_seconds || 0);
                      const estimatedMins = (Number(profile.estimated_training_time_seconds || 0) / 60).toFixed(0);
                      return (
                        <div
                          key={profileId}
                          className="rounded-lg border border-dashed border-yellow-500/40 bg-yellow-500/5 p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {profile.label}
                                <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-300">
                                  🔒 Premium
                                </span>
                              </p>
                              <p className="text-xs text-gray-400">
                                Min {profileMinDuration.toFixed(0)}s • Est {estimatedMins}m
                              </p>
                              <p className="text-xs text-yellow-200 mt-1">
                                {profile.upsell_message || 'Upgrade to unlock this profile.'}
                              </p>
                            </div>
                            <a href="/billing" className="btn btn-secondary text-xs whitespace-nowrap">
                              Upgrade
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Upload Samples - Only if pending or failed */}
              {['pending', 'failed'].includes(selectedClone.training_status) && (
                <div className="card">
                  <h3 className="font-semibold mb-4">Upload Voice Samples</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Upload clear audio recordings of the voice you want to clone.
                    Minimum {requiredDuration.toFixed(0)} seconds required for selected target.
                    For best results, record 1-3 minutes.
                  </p>
                  <SampleUploader 
                    cloneId={selectedClone.id} 
                    onSampleUploaded={handleSampleUploaded}
                  />
                </div>
              )}

              {/* Training Prompts - shown when pending */}
              {selectedClone.training_status === 'pending' && trainingConfig?.training_prompts && (
                <TrainingPrompts prompts={trainingConfig.training_prompts} />
              )}

              {/* Actions */}
              <div className="card">
                <h3 className="font-semibold mb-4">Actions</h3>
                <div className="flex flex-wrap gap-3">
                  {selectedClone.training_status === 'pending' && 
                   selectedClone.total_sample_duration >= requiredDuration && (
                     <button
                       onClick={() => handleStartTraining(selectedClone.id, selectedTargetQuality)}
                       className="btn btn-primary"
                     >
                       🚀 Start Training
                     </button>
                   )}
                   
                   {selectedClone.training_status === 'pending' && 
                   selectedClone.total_sample_duration < requiredDuration && (
                     <p className="text-yellow-400 text-sm">
                       ⚠️ Need {(requiredDuration - selectedClone.total_sample_duration).toFixed(1)}s more audio to start training
                     </p>
                   )}

                  {selectedClone.training_status === 'completed' && (
                    <div className="w-full p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 font-medium mb-1">
                        ✓ Voice clone ready!
                      </p>
                      <p className="text-sm text-gray-400 mb-3">
                        Go to <a href="/text-to-speech" className="text-purple-400 hover:underline">Text-to-Speech</a> and 
                        select "{selectedClone.name}" from the voice list to use your cloned voice.
                      </p>
                      <button
                        onClick={() => handlePreviewClone(selectedClone.id)}
                        className="btn btn-secondary flex items-center gap-2"
                        disabled={isPreviewLoading}
                      >
                        {isPreviewLoading ? (
                          <>
                            <span className="spinner w-4 h-4"></span>
                            Loading...
                          </>
                        ) : isPreviewPlaying ? (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="6" y="6" width="4" height="12" />
                              <rect x="14" y="6" width="4" height="12" />
                            </svg>
                            Stop Preview
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                            Preview Voice
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {selectedClone.training_status === 'failed' && (
                    <div className="w-full p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 font-medium mb-1">
                        ✗ Training failed
                      </p>
                      <p className="text-sm text-gray-300 mb-3 whitespace-pre-line">
                        {selectedClone.training_error?.trim() ||
                          'Please try uploading clearer audio samples and start training again.'}
                      </p>
                      <button
                        onClick={() => handleStartTraining(selectedClone.id, selectedTargetQuality)}
                        className="btn btn-primary text-sm"
                      >
                        🔄 Retry Training
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => handleDeleteClone(selectedClone.id)}
                    className="btn btn-secondary text-red-400 hover:text-red-300"
                    disabled={selectedClone.training_status === 'training'}
                  >
                    🗑 Delete Clone
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="card text-center py-16">
              <div className="text-5xl mb-4">👈</div>
              <p className="text-gray-400">
                Select a voice clone from the list or create a new one
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">Create Voice Clone</h2>
            
            <form onSubmit={handleCreateClone} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Voice Name *
                </label>
                <input
                  type="text"
                  value={newCloneName}
                  onChange={(e) => setNewCloneName(e.target.value)}
                  placeholder="e.g., My Voice, John's Voice"
                  className="input w-full"
                  maxLength={100}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={newCloneDescription}
                  onChange={(e) => setNewCloneDescription(e.target.value)}
                  placeholder="Add notes about this voice..."
                  className="textarea w-full h-24"
                  maxLength={500}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <span className="flex items-center">
                      <span className="spinner w-4 h-4 mr-2"></span>
                      Creating...
                    </span>
                  ) : (
                    'Create'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default VoiceCloning;
